import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { LoadingForType, NotificationType, usePersona, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { ButtonGroup, Spacer, Spinner } from "@nextui-org/react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { LocalizedLink, PageLayout, PageSize } from "@/components";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import WarningModal from "@/components/common/WarningModal";
import { Col, Grid, Row, SpicyButton, SpicyInput } from "@/components/customComponents";
import LimitReachedModal from "@/components/Personas/LimitReachedModal";
import MyPersonasCard from "@/components/Personas/MyPersonasCard";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import useToast from "@/hooks/useToast";
import { isPersonasLimitReached } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

import { PERSONA_ERRORS } from "./contants";

export default function PersonasLoggedIn() {
  const { t } = useTranslation();
  const { role } = useUserInfo();
  const {
    personas,
    defaultPersonaId,
    loading,
    success,
    resetSuccess,
    onUserStartDeletingPersona,
    isDeletingPersonaStarted,
    onUserConfirmDeletingPersona,
    onUserCancelDeletingPersona,
    onUserSelectsPersonaToDelete,
    onUserMakePersonaToDefault,
    errorType: personaErrorType,
  } = usePersona();
  const [searchQuery, setSearchQuery] = useState("");
  const filteredPersonas = personas?.filter((d) => d?.name.toLowerCase().startsWith(searchQuery?.trim()?.toLowerCase()));
  const location = useLocation();
  const prevPage = location.state?.path?.split("/")[1];
  const characterName = location.state?.characterName;
  const navigate = useNavigate();
  const [isLimitReachedModal, setIsLimitReachedModal] = useState(false);
  const [isShowWarningModal, setIsShowWarningModal] = useState(false);
  const isDeleteLoading = loading?.loadingType === LoadingForType.deletePersona && loading.isLoading;
  const edit = false;
  const isPartner = getIsPartner();
  const { displayToast } = useToast();

  useEffect(() => {
    if (personaErrorType) {
      displayToast({ message: PERSONA_ERRORS[personaErrorType], type: NotificationType.error });
    }
  }, [personaErrorType]);

  const clearInput = useCallback(() => {
    setSearchQuery("");
  }, []);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, []);

  const handleCreateNew = () => {
    if (isPersonasLimitReached(personas, role, edit, isPartner)) {
      setIsLimitReachedModal(true);
    }
  };

  const handleClose = useCallback(() => {
    onUserCancelDeletingPersona();
  }, []);

  const onDeleteConfirmation = async () => {
    await onUserConfirmDeletingPersona();
  };

  const handleWarningModalClose = () => {
    setIsShowWarningModal(false);
  };

  useEffect(() => {
    if (loading?.loadingType === LoadingForType?.deletePersona && success) {
      displayToast({ message: t("personas:page.deletePersona.toast.success"), type: NotificationType.success });
      resetSuccess();
    }
  }, [success]);

  if (!personas.length) {
    return <Spinner className="absolute top-1/2 left-1/2 scale-[1.5]" />;
  }
  return (
    <PageLayout size={PageSize.Medium}>
      <Col className="min-h-[calc(100vh-55px)] custom-scroll">
        <Col className="grow">
          <Col className="*:p-0">
            <Text variant={TextVariants.HEADING_5_MEDIUM} className="tracking-tighter pl-3">
              <Trans i18nKey="personas:page.title" />
            </Text>
          </Col>

          <Row style={undefined} justify="between">
            <Grid className="max-w-[700px] grow">
              <SpicyInput
                value={searchQuery}
                variant="primary"
                color="primary"
                placeholder={`${t("common:search")}...`}
                onValueChange={setSearchQuery}
                isClearable
                onClear={clearInput}
                aria-labelledby="search-characters"
              />
            </Grid>
          </Row>

          <Spacer y={6} />

          {!filteredPersonas?.length ? (
            <Text>
              <Trans i18nKey="personas:page.noResultFound" />
            </Text>
          ) : (
            <Grid className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] max-sm:grid-cols-2 gap-5">
              {filteredPersonas
                ?.sort((a, b) => (a.id === defaultPersonaId ? -1 : b.id === defaultPersonaId ? 1 : 0))
                ?.map((data) => (
                  <MyPersonasCard
                    key={data.id}
                    data={data}
                    defaultPersonaId={defaultPersonaId ?? ""}
                    loading={loading ?? undefined}
                    onUserStartDeletingPersona={onUserStartDeletingPersona}
                    onUserSelectsPersonaToDelete={onUserSelectsPersonaToDelete}
                    onUserMakePersonaToDefault={onUserMakePersonaToDefault}
                    setIsShowWarningModal={setIsShowWarningModal}
                  />
                ))}
            </Grid>
          )}
        </Col>

        <ButtonGroup className="flex flex-wrap justify-end gap-4 sticky bottom-0 pt-6 pb-8 z-[100]">
          {prevPage === "chat" ? (
            <>
              <SpicyButton as={LocalizedLink} variant="primary_large_bordered" color="transparent" to={ROUTES.createPersona}>
                <Text>
                  <Trans i18nKey="personas:page.button.createNew" />
                </Text>
              </SpicyButton>
              <SpicyButton as={LocalizedLink} variant="primary_large" color="primary_strong" onPress={handleGoBack}>
                <Text>
                  <Trans i18nKey="personas:page.button.goBack" /> {characterName}
                </Text>
              </SpicyButton>
            </>
          ) : (
            <SpicyButton
              as={LocalizedLink}
              variant="primary_large"
              color="primary_strong"
              onClick={handleCreateNew}
              to={isPersonasLimitReached(personas, role, edit, isPartner) ? undefined : ROUTES.createPersona}
            >
              <Text>
                <Trans i18nKey="personas:page.button.createNew" />
              </Text>
            </SpicyButton>
          )}
        </ButtonGroup>
      </Col>
      <LimitReachedModal isOpen={isLimitReachedModal} onClose={() => setIsLimitReachedModal(false)} />

      {!!isDeletingPersonaStarted && (
        <ConfirmationModal
          heading={t("personas:modal.deletePersona.title")}
          handleClose={handleClose}
          onUserConfirmation={onDeleteConfirmation}
          confirmationMessage={
            <>
              <Text>
                <Trans i18nKey="personas:modal.deletePersona.description" />
              </Text>
              <Spacer y={2} />
              <Text>
                <Trans i18nKey="personas:modal.deletePersona.confirmationMessage" />
              </Text>
            </>
          }
          discardButtonText={t("common:cancel")}
          isVisible={isDeletingPersonaStarted}
          isLoading={isDeleteLoading}
          actionButtonText={t("common:delete")}
          actionButtonColor="error"
          titleClassNames="!pl-0"
        />
      )}

      {!!isShowWarningModal && (
        <WarningModal
          heading={t("personas:modal.deletePersonaWarning.title")}
          message={t("personas:modal.deletePersonaWarning.message")}
          isVisible={isShowWarningModal}
          onClose={handleWarningModalClose}
        />
      )}
    </PageLayout>
  );
}
